import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Modal } from "../ui/Modal/Modal";
import { LoadingSpinner } from "../ui/LoadingSpinner/LoadingSpinner";
import { ErrorFallback } from "../ui/ErrorFallback/ErrorFallback";
import { useProductionUnitDetail } from "../../hooks/use-orders";
import { formatDateTime } from "../../lib/date";
import styles from "./ProductionTrackingModal.module.css";

interface ProductionTrackingModalProps {
	orderId: string;
	serialNo: string | null;
	onClose: () => void;
}

const STATUS_LABEL_KEY: Record<string, string> = {
	done: "productionStatusDone",
	in_progress: "productionStatusInProgress",
	waiting: "productionStatusWaiting",
	paused: "productionStatusPaused",
	planned: "productionStatusPlanned",
};

const ACTION_LABEL_KEY: Record<string, string> = {
	start: "productionActionStart",
	finish: "productionActionFinish",
	pause: "productionActionPause",
	resume: "productionActionResume",
};

function formatDuration(startedAt: string | null, finishedAt: string | null): string {
	if (!startedAt || !finishedAt) return "-";
	const seconds = Math.max(
		0,
		Math.round((new Date(finishedAt).getTime() - new Date(startedAt).getTime()) / 1000),
	);
	if (seconds < 60) return `${seconds}sn`;
	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return `${minutes}dk`;
	const hours = Math.floor(minutes / 60);
	const remMinutes = minutes % 60;
	return `${hours}sa ${remMinutes}dk`;
}

export function ProductionTrackingModal({
	orderId,
	serialNo,
	onClose,
}: ProductionTrackingModalProps) {
	const { t } = useTranslation();
	const query = useProductionUnitDetail(orderId, serialNo);
	const [expanded, setExpanded] = useState<Record<string, boolean>>({});

	const detail = query.data;
	const stages = detail?.stages ?? [];
	const doneCount = stages.filter((s) => s.status === "done").length;
	const waitingCount = stages.filter(
		(s) => s.status === "waiting" || s.status === "in_progress" || s.status === "paused",
	).length;
	const plannedCount = stages.filter((s) => s.status === "planned").length;
	const percent = stages.length > 0 ? (doneCount / stages.length) * 100 : 0;

	return (
		<Modal isOpen={!!serialNo} onClose={onClose} title={t("productionTracking")} maxWidth={760}>
			{query.isLoading && <LoadingSpinner />}
			{query.isError && <ErrorFallback onRetry={query.refetch} />}
			{detail && (
				<>
					<div className={styles.subtitle}>
						{t("serialNo")}: <strong>{detail.serialNo}</strong> · {detail.itemCode} —{" "}
						{detail.itemName}
					</div>

					<div className={styles.progressSection}>
						<div className={styles.progressHeader}>
							<span>{t("productionProgress")}</span>
							<span>
								{doneCount} / {stages.length} {t("stagesCompleted")}
							</span>
						</div>
						<div className={styles.progressBar}>
							<div className={styles.progressFill} style={{ width: `${percent}%` }} />
						</div>
						<div className={styles.badgeRow}>
							<span className={styles.badgeDone}>
								{doneCount} {t("completed")}
							</span>
							<span className={styles.badgeWaiting}>
								{waitingCount} {t("waiting")}
							</span>
							<span className={styles.badgePlanned}>
								{plannedCount} {t("planned")}
							</span>
						</div>
					</div>

					<div className={styles.stageList}>
						{stages.map((stage) => {
							const isOpen = !!expanded[stage.stageCode];
							return (
								<div key={stage.stageCode} className={styles.stageItem}>
									<button
										type="button"
										className={styles.stageHeader}
										onClick={() =>
											setExpanded((prev) => ({
												...prev,
												[stage.stageCode]: !prev[stage.stageCode],
											}))
										}
									>
										<span className={[styles.statusDot, styles[`dot_${stage.status}`]].join(" ")} />
										<span className={styles.stageName}>{stage.stageName}</span>
										<span className={styles.stageDuration}>
											{formatDuration(stage.startedAt, stage.finishedAt)}
										</span>
										<span
											className={[styles.statusBadge, styles[`badge_${stage.status}`]].join(" ")}
										>
											{t(STATUS_LABEL_KEY[stage.status] ?? stage.status)}
										</span>
										{isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
									</button>
									{isOpen && (
										<div className={styles.stageDetail}>
											{(stage.startedAt || stage.finishedAt) && (
												<div className={styles.timeRow}>
													{stage.startedAt && (
														<div>
															<span className={styles.timeLabel}>{t("startedAt")}</span>
															<p>{formatDateTime(stage.startedAt)}</p>
														</div>
													)}
													{stage.finishedAt && (
														<div>
															<span className={styles.timeLabel}>{t("finishedAt")}</span>
															<p>{formatDateTime(stage.finishedAt)}</p>
														</div>
													)}
												</div>
											)}
											{stage.logs.length > 0 && (
												<table className={styles.logTable}>
													<thead>
														<tr>
															<th>{t("action")}</th>
															<th>{t("personnel")}</th>
															<th>{t("time")}</th>
															<th>{t("noteReason")}</th>
														</tr>
													</thead>
													<tbody>
														{stage.logs.map((log, idx) => (
															<tr key={idx}>
																<td>
																	<span className={styles.actionBadge}>
																		{t(ACTION_LABEL_KEY[log.action] ?? log.action)}
																	</span>
																</td>
																<td>{log.userName ?? "-"}</td>
																<td>{formatDateTime(log.createdAt)}</td>
																<td>{log.note || log.reason || "—"}</td>
															</tr>
														))}
													</tbody>
												</table>
											)}
										</div>
									)}
								</div>
							);
						})}
					</div>
				</>
			)}
		</Modal>
	);
}
